<template>
	<AssemblyLayout :assembly="assembly" :subtitle="trans('assemblies.close.button')">
		<template #actions>
			<PrimaryButton :color="Color.Red" :disabled="unassignedCandidates.length > 0" @click.stop="store">
				<i class="fa-solid fa-door-open mr-2"/>
				{{ trans('assemblies.close.button') }}
			</PrimaryButton>
		</template>
		<Card :title="trans('assemblies.close.assignations')">
			<p v-for="instruction in trans('assemblies.close.instructions')">{{ instruction }}</p>
			<div class="grid grid-cols-1 md:grid-cols-2 gap-3">
				<LabeledObject v-for="rank in ranks" full>
					<template #label>{{ rank.name }}</template>
					<div v-if="rank.legal_rank" class="input-like flex items-center gap-2">
						<div class="grow flex flex-col gap-1">
							<p class="text-gray-400 text-sm">{{ trans(`association.boards.legal_ranks.${rank.legal_rank}`) }}</p>
							<p>{{ rank.title }}</p>
						</div>
						<i class="fa-solid fa-circle-xmark text-red-400 dark:text-red-600 cursor-pointer" @click.stop="rank.legal_rank = null"/>
					</div>
					<Dropdown v-else :id="`assign-rank-${rank.id}`">
						<template #button>
							<PrimaryButton :color="Color.Blue">
								{{ trans('assemblies.close.assign') }}
							</PrimaryButton>
						</template>
						<DropdownItem v-for="candidate in unassignedCandidates" @click.stop="assign(rank, candidate)">
							<div class="flex flex-col gap-1">
								<p class="text-gray-400 text-sm">{{ trans(`association.boards.legal_ranks.${candidate.legalRank}`) }}</p>
								<p>{{ candidate.title }}</p>
							</div>
						</DropdownItem>
					</Dropdown>
				</LabeledObject>
			</div>
		</Card>
	</AssemblyLayout>
</template>
<script setup lang="ts">
import {route} from 'ziggy-js'
import {trans} from 'laravel-vue-i18n'
import {useForm} from '@inertiajs/vue3'
import {Color} from '@/Types/Enums/Color'
import Card from '@/Components/UI/Cards/Card.vue'
import {type Rank} from '@/Types/Association/Rank'
import AssemblyLayout from '@/Layouts/AssemblyLayout.vue'
import Dropdown from '@/Components/UI/Dropdown/Dropdown.vue'
import {computed, type ComputedRef, ref, type Ref} from 'vue'
import LabeledObject from '@/Components/UI/LabeledObject.vue'
import DropdownItem from '@/Components/UI/Dropdown/DropdownItem.vue'
import PrimaryButton from '@/Components/UI/Buttons/PrimaryButton.vue'
import {type Assembly} from '@/Types/Association/Assemblies/Assembly'
import {showInfo, showSuccess, showValidationErrors} from '@/Helpers/general'
import {type ElectedCandidate} from '@/Types/Association/Assemblies/ElectedCandidate'

const props = defineProps<{
	ranks:Array<Rank>,
	assembly:Assembly,
	electedCandidates:Array<ElectedCandidate>,
}>()

const working:Ref<boolean> = ref(false)
const ranks:Ref<Array<Rank>> = ref(props.ranks)
const assembly:Ref<Assembly> = ref(props.assembly)
const electedCandidates:Ref<Array<ElectedCandidate>> = ref(props.electedCandidates)

const unassignedCandidates:ComputedRef<Array<ElectedCandidate>> = computed(():Array<ElectedCandidate> => {
	return electedCandidates.value.filter((candidate:ElectedCandidate) => {
		return ranks.value.findIndex((rank:Rank) => rank.legal_rank === candidate.legalRank) === -1
	})
})

function assign(rank:Rank, electedCandidate:ElectedCandidate):void{
	rank.legal_rank = electedCandidate.legalRank
	rank.title = electedCandidate.title
}

function store():void{
	if(!working.value && confirm(trans('general.confirm_action'))){
		working.value = true
		showInfo(trans('general.updating'))
		useForm({
			ranks: ranks.value,
		}).post(route('association.assemblies.finalization.store', assembly.value.id), {
			onSuccess: () => {
				showSuccess(trans('general.updated'))
			},
			onError: showValidationErrors,
			onFinish: () => {
				working.value = false
			},
		})
	}
}
</script>